import React, { useState } from 'react'
import type { UnitData } from '../data/mockSpaceRevenue'
import { Button } from '@/components/ui/button'
import { Filter, ArrowUpDown, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export const UnitDetailTable = ({ units }: { units: UnitData[] }) => {
    const [groupBy, setGroupBy] = useState<'none' | 'building'>('building')
    
    // Simple grouping logic
    const groupedUnits = React.useMemo(() => {
        if (groupBy === 'none') return { 'All Units': units }
        
        return units.reduce((acc, unit) => {
            const key = unit.building_name
            if (!acc[key]) acc[key] = []
            acc[key].push(unit)
            return acc
        }, {} as Record<string, UnitData[]>)
    }, [units, groupBy])

    return (
    <div className="space-y-4">
        <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold tracking-tight">Unit Detail Table</h2>
            <div className="flex gap-2">
                 <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8">
                            <Filter className="w-3.5 h-3.5 mr-2" /> 
                            Group: {groupBy === 'building' ? 'Building' : 'None'}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setGroupBy('building')}>Building</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setGroupBy('none')}>None</DropdownMenuItem>
                    </DropdownMenuContent>
                 </DropdownMenu>
                 <Button variant="outline" size="sm" className="h-8">
                    <ArrowUpDown className="w-3.5 h-3.5 mr-2" /> Sort
                 </Button>
            </div>
        </div>

        <div className="border rounded-md bg-white shadow-sm overflow-hidden">
            <table className="w-full text-sm">
                <thead>
                    <tr className="bg-muted/50 border-b text-left">
                        <th className="h-10 px-4 font-medium text-muted-foreground w-[100px]">Unit</th>
                        <th className="h-10 px-4 font-medium text-muted-foreground w-[100px]">Type</th>
                        <th className="h-10 px-4 font-medium text-muted-foreground w-[100px]">Sqft</th>
                        <th className="h-10 px-4 font-medium text-muted-foreground w-[100px]">Door</th>
                        <th className="h-10 px-4 font-medium text-muted-foreground w-[120px]">Status</th>
                        <th className="h-10 px-4 font-medium text-muted-foreground">Tenant</th>
                        <th className="h-10 px-4 font-medium text-muted-foreground">Aircraft</th>
                        <th className="h-10 px-4 font-medium text-muted-foreground text-right">Mo. Rate</th>
                        <th className="h-10 px-4 font-medium text-muted-foreground text-right">$/Sqft</th>
                        <th className="h-10 px-4 font-medium text-muted-foreground text-right">Expires</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {Object.entries(groupedUnits).map(([groupName, groupUnits]) => (
                        <React.Fragment key={groupName}>
                            {/* Group Header */}
                            {groupBy !== 'none' && (
                                <tr className="bg-slate-50/80 hover:bg-slate-50">
                                    <td colSpan={10} className="px-4 py-2 font-semibold text-slate-700">
                                        <div className="flex items-center gap-2">
                                            <ChevronDown className="w-4 h-4 text-slate-400" />
                                            {groupName} 
                                            <span className="font-normal text-muted-foreground text-xs ml-2">
                                                {groupUnits.length} units · {groupUnits.reduce((a,u) => a + u.sqft, 0).toLocaleString()} sqft
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            
                            {/* Rows */}
                            {groupUnits.map(u => {
                                const ratePerSqft = u.monthly_rate ? (u.monthly_rate / u.sqft).toFixed(2) : '-'
                                const expiryDate = u.contract_end_date ? new Date(u.contract_end_date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '-'
                                
                                return (
                                    <tr key={u.id} className="hover:bg-slate-50 transition-colors group">
                                        <td className="p-4 font-medium">{u.unit_code}</td>
                                        <td className="p-4 text-muted-foreground">{u.unit_type}</td>
                                        <td className="p-4">{u.sqft.toLocaleString()}</td>
                                        <td className="p-4 text-muted-foreground">
                                            {u.door_width && u.door_height ? `${u.door_width}×${u.door_height}` : '-'}
                                        </td>
                                        <td className="p-4">
                                            <StatusBadge status={u.status} />
                                        </td>
                                        <td className="p-4 font-medium text-slate-700">{u.tenant_name || '—'}</td>
                                        <td className="p-4">
                                            {u.aircraft_tail ? (
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{u.aircraft_tail}</span>
                                                    <span className="text-xs text-muted-foreground">{u.aircraft_type}</span>
                                                </div>
                                            ) : '—'}
                                        </td>
                                        <td className="p-4 text-right tabular-nums">
                                            {u.monthly_rate ? `$${u.monthly_rate.toLocaleString()}` : '—'}
                                        </td>
                                        <td className={cn("p-4 text-right tabular-nums font-medium", 
                                            u.status === 'underpriced' ? "text-orange-600" : ""
                                        )}>
                                            {ratePerSqft !== '-' ? `$${ratePerSqft}` : '—'}
                                        </td>
                                        <td className="p-4 text-right tabular-nums text-muted-foreground">
                                            {expiryDate}
                                            {u.days_to_expiry !== undefined && u.days_to_expiry < 90 && (
                                                <div className="text-[10px] font-bold text-amber-600">
                                                    {u.days_to_expiry} days
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </React.Fragment>
                    ))}
                </tbody>
                 {/* Footer Totals */}
                 <tfoot className="bg-slate-100/50 border-t font-medium text-slate-700">
                    <tr>
                        <td colSpan={2} className="px-4 py-3">TOTALS</td>
                        <td className="px-4 py-3">{units.reduce((a,u) => a + u.sqft, 0).toLocaleString()}</td>
                        <td colSpan={4}></td>
                        <td className="px-4 py-3 text-right">
                             ${units.reduce((a,u) => a + (u.monthly_rate || 0), 0).toLocaleString()}
                        </td>
                         <td className="px-4 py-3 text-right">
                            ${(units.reduce((a,u) => a + (u.monthly_rate || 0), 0) / units.reduce((a,u) => a + u.sqft, 0)).toFixed(2)} avg
                        </td>
                        <td></td>
                    </tr>
                 </tfoot>
            </table>
        </div>
    </div>
    )
}

const StatusBadge = ({ status }: { status: UnitData['status'] }) => {
    switch (status) {
        case 'leased':
            return <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5" />
                Leased
            </div>
        case 'underpriced':
             return <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                <div className="w-1.5 h-1.5 rounded-full bg-orange-500 mr-1.5" />
                Underpriced
            </div>
        case 'transient':
             return <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-1.5" />
                Transient
            </div>
        case 'expiring':
             return <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <div className="w-1.5 h-1.5 rounded-full bg-yellow-500 mr-1.5" />
                Expiring
            </div>
        case 'vacant':
             return <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-1.5" />
                Vacant
            </div>
         case 'maintenance':
             return <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-800 text-slate-100">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-400 mr-1.5" />
                Maint
            </div>
        default:
             return <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {status}
            </div>
    }
}
